import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Componente para capturar errores de React
 *
 * Features:
 * - Captura errores en componentes hijos
 * - UI de fallback amigable
 * - Logging de errores
 * - Botón de reset
 * - Integración con error tracking (Sentry, etc.)
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error

    // Update state
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example:
    // Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    this.handleReset();
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-8 text-white">
                <div className="flex items-center justify-center mb-4">
                  <ExclamationTriangleIcon className="h-16 w-16" />
                </div>
                <h1 className="text-3xl font-bold text-center">
                  Algo salió mal
                </h1>
                <p className="text-red-100 text-center mt-2">
                  Ocurrió un error inesperado en la aplicación
                </p>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6">
                {/* Error message */}
                {this.state.error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                      Mensaje de error:
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-300 font-mono">
                      {this.state.error.toString()}
                    </p>
                  </div>
                )}

                {/* Error details (dev only) */}
                {import.meta.env.DEV && this.state.errorInfo && (
                  <details className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <summary className="text-sm font-semibold text-gray-900 dark:text-white cursor-pointer mb-2">
                      Detalles técnicos (dev)
                    </summary>
                    <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-auto max-h-64 mt-2">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                    ¿Qué puedes hacer?
                  </h3>
                  <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
                    <li>• Intenta recargar la página</li>
                    <li>• Si el error persiste, regresa al inicio</li>
                    <li>• Reporta el error si continúa ocurriendo</li>
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={this.handleReset}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Reintentar
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <HomeIcon className="h-4 w-4" />
                    Ir al Inicio
                  </button>
                </div>
              </div>
            </div>

            {/* Footer info */}
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
              El error ha sido registrado automáticamente
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
